import { useMemo } from 'react'
import { Clock, RefreshCw, Calendar, Coins, Flag, Send, Check } from 'lucide-react'

export default function ConfiguracionProgramacion({ formState, setFormState, handleSave, saving }) {
  const handleChange = (field, value) => {
    setFormState(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value
      }
    }))
  }

  const handleFrequencyChange = e => {
    const newFrequency = e.target.value
    setFormState(prev => ({
      ...prev,
      config: {
        ...prev.config,
        frequency: newFrequency,
        // Reset dependent fields
        start_date: new Date().toISOString().split('T')[0],
        day_of_month: 1,
        day_of_week: 'monday',
        emission_day_adjustment: 'none',
        end_type: 'never',
        end_date: '',
        max_occurrences: null
      }
    }))
  }

  const {
    frequency,
    start_date: startDate,
    end_type: endType,
    day_of_month: dayOfMonth,
    day_of_week: dayOfWeek
  } = formState.config

  const summaryData = useMemo(() => {
    // Label for Frequency
    const freqLabels = {
      daily: 'Diaria',
      weekly: 'Semanal',
      monthly: 'Mensual',
      quarterly: 'Trimestral',
      semiannual: 'Semestral',
      yearly: 'Anual'
    }
    const frequencyLabel = freqLabels[frequency] || frequency

    // Label for End Date
    let endDateLabel = 'Nunca'
    if (endType === 'on_date') endDateLabel = 'Fecha específica'
    if (endType === 'after_occurrences') endDateLabel = 'Después de X repeticiones'

    // Next Emission Dates Calculation (Simplified for UI display)
    const nextDates = []
    const start = startDate ? new Date(startDate + 'T00:00:00') : new Date()

    // Helper to add days/months/years
    const addTime = (date, count, unit) => {
      const d = new Date(date)
      if (unit === 'days') d.setDate(d.getDate() + count)
      if (unit === 'months') d.setMonth(d.getMonth() + count)
      if (unit === 'years') d.setFullYear(d.getFullYear() + count)
      return d
    }

    // Generate 3 sample future dates
    let currentDate = new Date(start)
    for (let i = 0; i < 3; i++) {
      // Simple increment logic for demo purposes
      if (frequency === 'daily') currentDate = addTime(currentDate, 1, 'days')
      if (frequency === 'weekly') currentDate = addTime(currentDate, 7, 'days')
      if (frequency === 'monthly') currentDate = addTime(currentDate, 1, 'months')
      if (frequency === 'quarterly') currentDate = addTime(currentDate, 3, 'months')
      if (frequency === 'semiannual') currentDate = addTime(currentDate, 6, 'months')
      if (frequency === 'yearly') currentDate = addTime(currentDate, 1, 'years')

      nextDates.push(currentDate.toLocaleDateString('es-CL'))
    }

    return {
      frequencyLabel,
      endDateLabel,
      nextDates
    }
  }, [frequency, startDate, endType, dayOfMonth, dayOfWeek])

  return (
    <div className='space-y-4'>
      {/* Main Configuration Card */}
      <div className='bg-white p-5 rounded-lg shadow-sm border border-gray-100'>
        <div className='section-title'>
          <Clock size={20} className='text-primary' />
          <h3>Configuración de Programación</h3>
        </div>

        <div className='space-y-4'>
          {/* Frequency */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Frecuencia</label>

            <select
              value={formState.config.frequency}
              onChange={handleFrequencyChange}
              className='input w-full p-2'
            >
              <option value='daily'>Diario</option>
              <option value='weekly'>Semanal</option>
              <option value='monthly'>Mensual</option>
              <option value='quarterly'>Trimestral</option>
              <option value='semiannual'>Semestral</option>
              <option value='yearly'>Anual</option>
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Fecha de Inicio <span className='text-gray-400 font-normal'>(Opcional)</span>
            </label>

            <input
              type='date'
              value={formState.config.start_date}
              onChange={e => handleChange('start_date', e.target.value)}
              className='input w-full p-2'
            />

            <p className='text-xs text-gray-500 mt-1'>
              <span className='font-bold'>Solo para nuevos documentos:</span> Fecha específica en la
              que comenzará la programación.
            </p>
            <p className='text-xs text-blue-500 mt-1 flex gap-1'>
              💡 Si se deja vacío, la programación comenzará según la configuración de frecuencia
              desde hoy.
            </p>
          </div>

          {/* Day of Month - Hidden for daily/weekly */}
          {formState.config.frequency !== 'daily' && formState.config.frequency !== 'weekly' && (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Día del mes</label>
              <select
                value={formState.config.day_of_month}
                onChange={e => handleChange('day_of_month', e.target.value)}
                className='input w-full p-2'
              >
                {[...Array(31)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              <div className='text-xs text-gray-500 mt-1 space-y-0.5'>
                <p>
                  <span className='font-bold'>Importante:</span> Si seleccionas el día 31, se
                  ajustará automáticamente:
                </p>
                <p>• Noviembre → día 30 (noviembre tiene solo 30 días)</p>
                <p>• Febrero → día 28 o 29 (según año bisiesto)</p>
                <p>• Abril, junio, septiembre → día 30</p>
              </div>
            </div>
          )}

          {/* Day of Week - Only for weekly */}
          {formState.config.frequency === 'weekly' && (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Día de la semana
              </label>
              <select
                value={formState.config.day_of_week || 'monday'}
                onChange={e => handleChange('day_of_week', e.target.value)}
                className='input w-full p-2'
              >
                <option value='monday'>Lunes</option>
                <option value='tuesday'>Martes</option>
                <option value='wednesday'>Miércoles</option>
                <option value='thursday'>Jueves</option>
                <option value='friday'>Viernes</option>
                <option value='saturday'>Sábado</option>
                <option value='sunday'>Domingo</option>
              </select>
            </div>
          )}

          {/* Business Day Adjustment - Not for semiannual */}
          {formState.config.frequency !== 'semiannual' && (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Ajuste de Día Hábil
              </label>
              <select
                value={formState.config.emission_day_adjustment}
                onChange={e => handleChange('emission_day_adjustment', e.target.value)}
                className='input w-full p-2'
              >
                <option value='none'>Sin ajuste</option>
                <option value='next'>Próximo día hábil</option>
                <option value='previous'>Anterior día hábil</option>
              </select>
              <p className='text-xs text-gray-500 mt-1'>
                Si la fecha programada cae en fin de semana o feriado, se ajustará automáticamente
                al día hábil más cercano.
              </p>
            </div>
          )}

          {/* End Date */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Finalización</label>
            <select
              value={formState.config.end_type}
              onChange={e => handleChange('end_type', e.target.value)}
              className='input w-full p-2'
            >
              <option value='never'>Nunca</option>
              <option value='on_date'>En fecha específica</option>
              <option value='after_occurrences'>Después de X repeticiones</option>
            </select>

            {formState.config.end_type === 'on_date' && (
              <div className='mt-2'>
                <label className='block text-xs font-medium text-gray-600 mb-1'>Fecha fin</label>
                <input
                  type='date'
                  value={formState.config.end_date}
                  onChange={e => handleChange('end_date', e.target.value)}
                  className='input w-full p-2'
                />
              </div>
            )}
            {formState.config.end_type === 'after_occurrences' && (
              <div className='mt-2'>
                <input
                  type='number'
                  min='1'
                  placeholder='Número de repeticiones'
                  value={formState.config.max_occurrences ?? ''}
                  onChange={e => handleChange('max_occurrences', e.target.value)}
                  className='input w-full p-2'
                />
                <p className='text-xs text-gray-500 mt-1'>
                  Se detendrá después de esta cantidad de ejecuciones.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resumen de Programación */}
      <div className='bg-slate-50 p-6 rounded-2xl border border-slate-100 text-sm space-y-4'>
        <h4 className='font-bold text-slate-800 text-base'>Resumen de programación</h4>
        <ul className='space-y-3'>
          <li className='flex items-center gap-2 text-slate-600'>
            <RefreshCw size={14} className='text-accent2' />
            <span className='font-medium text-slate-500'>Frecuencia:</span>{' '}
            <span className='text-slate-800 font-semibold'>{summaryData.frequencyLabel}</span>
          </li>
          {formState.config.frequency === 'monthly' && (
            <li className='flex items-center gap-2 text-slate-600'>
              <Calendar size={14} className='text-accent2' />
              <span className='font-medium text-slate-500'>Día del mes:</span>{' '}
              <span className='text-slate-800 font-semibold'>{formState.config.day_of_month}</span>
            </li>
          )}
          {formState.config.frequency === 'weekly' && (
            <li className='flex items-center gap-2 text-slate-600'>
              <Calendar size={14} className='text-accent2' />
              <span className='font-medium text-slate-500'>Día de la semana:</span>{' '}
              <span className='text-slate-800 font-semibold'>
                {(() => {
                  const days = {
                    monday: 'Lunes',
                    tuesday: 'Martes',
                    wednesday: 'Miércoles',
                    thursday: 'Jueves',
                    friday: 'Viernes',
                    saturday: 'Sábado',
                    sunday: 'Domingo'
                  }
                  return days[formState.config.day_of_week || 'monday']
                })()}
              </span>
            </li>
          )}
          {formState.currency === 'UF' && (
            <li className='flex items-center gap-2 text-slate-600'>
              <Coins size={14} className='text-accent2' />
              <span className='font-medium text-slate-500'>Día cambio UF:</span>{' '}
              <span className='text-slate-800 font-semibold'>
                Día {formState.config.currency_day || 1} de cada mes
              </span>
            </li>
          )}
          <li className='flex items-center gap-2 text-slate-600'>
            <Flag size={14} className='text-accent2' />
            <span className='font-medium text-slate-500'>Finalización:</span>{' '}
            <span className='text-slate-800 font-semibold'>{summaryData.endDateLabel}</span>
          </li>
          <li className='flex flex-col gap-1 text-slate-600'>
            <div className='flex items-center gap-2'>
              <Send size={14} className='text-accent2' />
              <span className='font-medium text-slate-500'>Próximas emisiones:</span>
            </div>
            <div className='pl-5'>
              <span className='text-slate-800 font-semibold text-xs'>
                {summaryData.nextDates.join(', ')}...
              </span>
            </div>
          </li>
        </ul>
      </div>

      <div className='flex justify-end gap-3'>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex gap-2 items-center cursor-pointer justify-center rounded-lg bg-primary px-6 py-2.5 text-xs font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-primary/90 active:scale-95 ${saving ? 'opacity-50 cursor-not-allowed' : ''
            }`}
        >
          {saving ? (
            <>
              <div className='animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full'></div>
              Guardando...
            </>
          ) : (
            <>
              <Check size={18} /> Guardar Cambios
            </>
          )}
        </button>
      </div>

      <style>{`
          .input {
            width: 100%;
            padding: 0.35rem 0.6rem;
            border: 1px solid #d1d5db;
            padding: 8px;
            border-radius: 4px;
          }
          .btn-detalle {
            font-size: 0.85rem;
            font-weight: 600;
            border: 1px solid #d35400;
            cursor: pointer;
            transition: all 0.2s;
          }

          .btn-detalle:hover {
            background: #000033;      
            border-color: #000033;
          }
        `}</style>
    </div>
  )
}
